import { default as React, useMemo, useRef } from 'react';
import { OptimizelyProvider, createInstance, setLogLevel, enums, OptimizelyDecideOption, } from '@optimizely/react-sdk';
import { customErrorHandler } from '../utils/error-handler';
import EnvironmentUtils from '@spothero/utils/environment';
import useIsMobile from 'hooks/use-is-mobile';
import { useSelector } from 'react-redux';
import { getParkingType } from 'store/selectors/selectors';
import { generateImpression, IMMEDIATE_FEATURES } from 'utils/experiment';
import ErrorUtils from 'utils/error-utils';
import { getIsNewVisitor } from 'utils/web-experiment';
import { useIsTotalPriceEnabledForStateCountry } from 'hooks/use-is-total-price-enabled-for-state-country';
import { isTransientMonthlyEventOrPowerBookingParking } from 'segment/utils/parking-type';
const formatUserId = (userId) => {
    // Remove all double quotes regardless of position
    return userId.replace(/"/g, '');
};
const showOptimizelyDevLogs = false; // Set to true to see optimizely logs in console
export default function SHOptimizelyProvider({ children, optimizelyDataFile, environment, optimizelyUserId, optimizelyAttributes, config, onDecision: onDecisionCallback, }) {
    const isTotalPriceEnabledForStateCountry = useIsTotalPriceEnabledForStateCountry();
    const isMobileDevice = useIsMobile();
    const optimizelyClient = useRef(null);
    const isBrowser = typeof window !== 'undefined';
    const userData = useSelector(({ user }) => user.data);
    const parkingType = useSelector(getParkingType);
    const searchCitySlug = useSelector((state) => state?.city?.data?.slug);
    const user = useMemo(() => {
        if (!isBrowser) {
            return {
                id: formatUserId(optimizelyUserId),
                attributes: optimizelyAttributes || {},
            };
        }
        const attributes = {
            parkingType,
            client: isMobileDevice ? 'mobile-web' : 'web',
            environment: environment === 'production' ? 'production' : 'staging',
            email: userData?.email || null,
            staff: Boolean(userData?.isAdmin).toString(),
            shUserId: userData?.id || null,
            isBrowser,
            pageUrl: window?.location?.href,
            pagePath: window?.location?.pathname,
            pageReferrer: window?.document?.referrer || null,
            userAgent: window?.navigator?.userAgent,
            browser: EnvironmentUtils.getBrowserCategory(),
            ...(config?.clientIPAddress && {
                ip: config?.clientIPAddress,
            }),
            monthly: parkingType === 'monthly',
            searchCitySlug: searchCitySlug || '',
            isNewVisitor: getIsNewVisitor(),
            isTotalPriceEnabledForStateCountry,
        };
        return {
            id: formatUserId(optimizelyUserId),
            attributes,
        };
    }, [
        isBrowser,
        parkingType,
        isMobileDevice,
        environment,
        userData?.email,
        userData?.isAdmin,
        userData?.id,
        config?.clientIPAddress,
        searchCitySlug,
        optimizelyUserId,
        optimizelyAttributes,
        isTotalPriceEnabledForStateCountry,
    ]);
    if (!optimizelyClient.current) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onDecision = ({ type, userId, attributes, decisionInfo }) => {
            const { decisionEventDispatched, flagKey, variationKey, } = decisionInfo;
            if (isBrowser &&
                type === 'flag' &&
                decisionEventDispatched &&
                (flagKey ===
                    IMMEDIATE_FEATURES.EXPRESS_CHECKOUT_WEB_DESKTOP_REFRESH || // temporarily accepting this even though just on/off
                    ['on', 'off'].includes(variationKey) === false) // Ignore on/off variations
            ) {
                onDecisionCallback({
                    variationKey,
                    flagKey,
                });
            }
        };
        // To prevent double impressions, we disable the decision event on the server side.
        const defaultDecideOptions = isBrowser
            ? []
            : [OptimizelyDecideOption.DISABLE_DECISION_EVENT];
        optimizelyClient.current = createInstance({
            datafile: optimizelyDataFile,
            errorHandler: customErrorHandler,
            defaultDecideOptions,
        });
        optimizelyClient.current
            .onReady()
            .then(() => {
            if (window?.location?.pathname === '/search' &&
                (parkingType === 'transient' || parkingType === 'event')) {
                generateImpression(optimizelyClient.current, IMMEDIATE_FEATURES.ACQUISITION_NEW_USER);
            }
            if (window?.location?.pathname === '/search' &&
                !isMobileDevice &&
                isTransientMonthlyEventOrPowerBookingParking(parkingType)) {
                generateImpression(optimizelyClient.current, IMMEDIATE_FEATURES.ACQUISITION_DESKTOP_WEB_DATETIME_PICKER);
            }
            // All In Pricing Impression
            if (window?.location?.pathname === '/search' &&
                !isTotalPriceEnabledForStateCountry &&
                isTransientMonthlyEventOrPowerBookingParking(parkingType)) {
                generateImpression(optimizelyClient.current, IMMEDIATE_FEATURES.ACQUISITION_ALL_IN_PRICING);
            }
        })
            .catch(error => {
            ErrorUtils.sendSentryMessage({
                error,
                customErrorMessage: 'OptimizelyProvider onReady error',
            });
        });
        if (showOptimizelyDevLogs)
            setLogLevel(enums.LOG_LEVEL.DEBUG);
        else
            setLogLevel(enums.LOG_LEVEL.ERROR);
        optimizelyClient.current.notificationCenter.addNotificationListener(enums.NOTIFICATION_TYPES.DECISION, onDecision);
    }
    return (<OptimizelyProvider optimizely={optimizelyClient.current} user={user} isServerSide={!isBrowser}>
            {children}
        </OptimizelyProvider>);
}
